import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  FileText,
  Users,
  TrendingUp,
  Mail,
  Brain,
  Bot,
  Clock,
  Activity,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { dashboardAPI } from '../lib/api';
import { formatNumber, formatPercentage, formatDateTime } from '../lib/utils';

function KPICard({ title, value, icon: Icon, subtitle, trend }) {
  return (
    <Card className="bg-surface-card border-border-default hover:border-accent-primary/50 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-text-tertiary uppercase tracking-wider mb-1">{title}</p>
            <p className="text-3xl font-heading font-bold text-text-primary">{value}</p>
            {subtitle && <p className="text-xs text-text-secondary mt-1">{subtitle}</p>}
            {trend && (
              <p className={`text-sm mt-2 ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {trend > 0 ? '+' : ''}{trend}% vs last period
              </p>
            )}
          </div>
          <div className="w-10 h-10 rounded-lg bg-accent-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-accent-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ActivityItem({ action, entityType, metadata, createdAt, userName }) {
  const getActionColor = (action) => {
    if (action.includes('created')) return 'text-green-400';
    if (action.includes('deleted')) return 'text-red-400';
    if (action.includes('sent')) return 'text-blue-400';
    return 'text-text-secondary';
  };

  return (
    <div className="flex items-start gap-3 py-3 border-b border-border-default last:border-0">
      <div className="w-2 h-2 rounded-full bg-accent-primary mt-2" />
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${getActionColor(action)}`}>
          {action.replace(/_/g, ' ')}
        </p>
        {entityType && (
          <p className="text-xs text-text-tertiary capitalize mt-0.5">
            {entityType.replace(/_/g, ' ')}
          </p>
        )}
        <p className="text-xs text-text-tertiary mt-1">
          {formatDateTime(createdAt)} {userName && `• ${userName}`}
        </p>
      </div>
    </div>
  );
}

export function Dashboard() {
  const [kpis, setKPIs] = useState(null);
  const [charts, setCharts] = useState(null);
  const [activity, setActivity] = useState([]);
  const [chartPeriod, setChartPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [chartPeriod]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [kpisRes, chartsRes, activityRes] = await Promise.all([
        dashboardAPI.getKPIs(),
        dashboardAPI.getCharts(chartPeriod),
        dashboardAPI.getActivity(20),
      ]);
      setKPIs(kpisRes.data);
      setCharts(chartsRes.data);
      setActivity(activityRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface-card border border-border-default rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-text-primary">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm text-text-secondary">
              {entry.name}: {formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading && !kpis) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-accent-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-text-primary uppercase tracking-tight">
          Dashboard
        </h1>
        <p className="text-text-secondary mt-1">Command Centre Overview</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Stories Published"
          value={formatNumber(kpis?.total_stories_published)}
          icon={FileText}
          subtitle="All time"
        />
        <KPICard
          title="This Month"
          value={formatNumber(kpis?.stories_this_month)}
          icon={TrendingUp}
          subtitle={`${kpis?.manual_stories_this_month || 0} manual / ${kpis?.automated_stories_this_month || 0} automated`}
        />
        <KPICard
          title="Active Clients"
          value={formatNumber(kpis?.total_active_clients)}
          icon={Users}
        />
        <KPICard
          title="Audience Contacts"
          value={formatNumber(kpis?.total_audience_contacts)}
          icon={Mail}
        />
        <KPICard
          title="Avg CTR"
          value={kpis?.avg_ctr_this_month ? formatPercentage(kpis.avg_ctr_this_month) : '-'}
          icon={TrendingUp}
          subtitle="This month"
        />
        <KPICard
          title="Messages Sent"
          value={formatNumber(kpis?.audience_messages_sent_this_month)}
          icon={Mail}
          subtitle="This month"
        />
        <KPICard
          title="Intelligence Records"
          value={formatNumber(kpis?.intelligence_records)}
          icon={Brain}
        />
        <KPICard
          title="Automated Runs"
          value={formatNumber(kpis?.automated_runs_today)}
          icon={Bot}
          subtitle="Today"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Charts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stories Over Time */}
          <Card className="bg-surface-card border-border-default">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading text-text-primary">
                  Stories Published
                </CardTitle>
                <Tabs value={chartPeriod} onValueChange={setChartPeriod}>
                  <TabsList className="bg-bg-tertiary">
                    <TabsTrigger value="weekly" className="text-xs">Weekly</TabsTrigger>
                    <TabsTrigger value="monthly" className="text-xs">Monthly</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64 min-h-[256px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <LineChart data={charts?.stories_over_time || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E2535" />
                    <XAxis 
                      dataKey="period" 
                      stroke="#64748B" 
                      tick={{ fill: '#64748B', fontSize: 11 }}
                    />
                    <YAxis 
                      stroke="#64748B" 
                      tick={{ fill: '#64748B', fontSize: 11 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Stories"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={{ fill: '#3B82F6', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Impressions by Channel */}
          <Card className="bg-surface-card border-border-default">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading text-text-primary">
                Impressions by Channel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 min-h-[256px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <BarChart data={charts?.impressions_by_channel || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E2535" />
                    <XAxis 
                      dataKey="channel" 
                      stroke="#64748B" 
                      tick={{ fill: '#64748B', fontSize: 11 }}
                      tickFormatter={(value) => value.replace('_', ' ')}
                    />
                    <YAxis 
                      stroke="#64748B" 
                      tick={{ fill: '#64748B', fontSize: 11 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="impressions" name="Impressions" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top Stories */}
          <Card className="bg-surface-card border-border-default">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading text-text-primary">
                Top Stories by Clicks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {charts?.top_stories?.length > 0 ? (
                  charts.top_stories.map((story, index) => (
                    <div
                      key={story.id}
                      className="flex items-center gap-4 p-3 bg-bg-tertiary rounded-lg"
                    >
                      <span className="text-lg font-heading font-bold text-accent-primary">
                        #{index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text-primary truncate">
                          {story.headline}
                        </p>
                      </div>
                      <Badge variant="secondary" className="bg-accent-primary/20 text-accent-primary">
                        {formatNumber(story.clicks)} clicks
                      </Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-text-tertiary text-center py-8">No story data available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity Log Sidebar */}
        <Card className="bg-surface-card border-border-default">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-accent-primary" />
              <CardTitle className="text-lg font-heading text-text-primary">
                Recent Activity
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px] pr-4">
              {activity.length > 0 ? (
                activity.map((item) => (
                  <ActivityItem
                    key={item.id}
                    action={item.action}
                    entityType={item.entity_type}
                    metadata={item.activity_metadata}
                    createdAt={item.created_at}
                    userName={item.user_name}
                  />
                ))
              ) : (
                <p className="text-text-tertiary text-center py-8">No recent activity</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Dashboard;
